/**
 * ReadInReaderBtn
 *
 * Calls POST /api/auth/reader-open → receives a signed 10-minute SSO
 * redirect URL → opens the Indie Reader in a new tab with the user
 * already logged in and (optionally) the chosen book open.
 *
 * Props:
 *   bookId        — bookstore book_id (optional; omit for library home)
 *   readerBookId  — pass directly if already resolved (optional)
 *   label         — button text (default: "Read in Indie Reader")
 *   variant       — "primary" | "ghost" (default: "primary")
 *   className     — extra Tailwind classes
 */
import React, { useState } from 'react';
import { BookOpen, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ReadInReaderBtn({
  bookId        = null,
  readerBookId  = null,
  label         = 'Read in Indie Reader',
  variant       = 'primary',
  className     = '',
  'data-testid': testId = 'read-in-reader-btn',
}) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleOpen = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/reader-open`, {
        book_id:        bookId,
        reader_book_id: readerBookId,
      });
      if (data?.redirect_url) {
        window.open(data.redirect_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Unable to open reader. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const base =
    variant === 'ghost'
      ? 'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-semibold transition hover:bg-[#8A4C3F]/10'
      : 'inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white transition hover:brightness-110';

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        data-testid={testId}
        className={`${base} disabled:opacity-50`}
        style={variant !== 'ghost' ? { backgroundColor: '#8A4C3F' } : { borderColor: '#8A4C3F', color: '#8A4C3F' }}
      >
        {loading
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <BookOpen className="h-4 w-4" />}
        {label}
        {!loading && <ExternalLink className="h-3.5 w-3.5 opacity-60" />}
      </button>
      {error && (
        <p className="mt-1 text-[11px] text-red-500" data-testid="read-in-reader-error">{error}</p>
      )}
    </div>
  );
}
